import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Sidebar from '../components/Sidebar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Settings, UserPlus, Edit2, Trash2, Users, Shield, 
  AlertCircle, Search, BadgeCheck, AlertTriangle, Loader2
} from 'lucide-react';
import api, { formatErrorMessage } from '../utils/api';
import { toast } from 'sonner';
import PermisosMatrix from './PermisosMatrix';
import ImportVentasTab from './ImportVentasTab';
import CompanyConfigTab from './CompanyConfigTab';
import FichasTecnicasCard from './FichasTecnicasCard';

const ROLES = [
  { value: 'Administrador', label: 'Administrador', description: 'Acceso total' },
  { value: 'Líder', label: 'Líder', description: 'Gestión de área y asignaciones' },
  { value: 'Coordinador', label: 'Coordinador', description: 'Supervisión de tareas' },
  { value: 'Usuario', label: 'Usuario', description: 'Visualización y tareas propias' },
];

const AREAS = [
  { value: 'Tributaria', label: 'Tributaria' },
  { value: 'Legal', label: 'Legal' },
  { value: 'Contabilidad', label: 'Contabilidad' },
  { value: 'Comercial', label: 'Comercial' },
  { value: 'Socios', label: 'Socios' },
  { value: 'Adm. y Finanzas', label: 'Adm. y Finanzas' },
];

const PanelControl = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewUser, setShowNewUser] = useState(false);
  const [showEditUser, setShowEditUser] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    password: '',
    role: 'Usuario',
    areas: [],
  });
  
  const [editForm, setEditForm] = useState({
    name: '',
    role: '',
    areas: [],
    password: '',
    email: '',
  });

  // Estados para limpieza de datos de prueba
  const [showCleanupDialog, setShowCleanupDialog] = useState(false);
  const [cleanupStep, setCleanupStep] = useState(1); // 1: primera confirmación, 2: segunda confirmación con password
  const [cleanupPassword, setCleanupPassword] = useState('');
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [cleanupResults, setCleanupResults] = useState(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await api.get('/users');
      setUsers(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('No tienes permisos para acceder a esta sección');
      } else {
        toast.error('Error al cargar usuarios');
      }
    } finally {
      setLoading(false);
    }
  };

  // Función para ejecutar limpieza de datos de prueba
  const handleCleanupData = async () => {
    if (!cleanupPassword) {
      toast.error('Ingrese su contraseña para confirmar');
      return;
    }
    
    setCleanupLoading(true);
    try {
      const response = await api.post('/admin/limpiar-datos-prueba', {
        admin_password: cleanupPassword
      });
      
      setCleanupResults(response.data.results);
      toast.success('Limpieza completada exitosamente');
      setCleanupStep(3); // Mostrar resultados
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Contraseña incorrecta');
      } else if (error.response?.status === 403) {
        toast.error('No tienes permisos para esta acción');
      } else {
        toast.error(formatErrorMessage(error));
      }
    } finally {
      setCleanupLoading(false);
    }
  };

  // Resetear modal de limpieza
  const resetCleanupDialog = () => {
    setShowCleanupDialog(false);
    setCleanupStep(1);
    setCleanupPassword('');
    setCleanupResults(null);
  };

  const handleCreateUser = async () => {
    if (!newUser.name || !newUser.email || !newUser.password) {
      toast.error('Complete todos los campos requeridos');
      return;
    }
    
    try {
      await api.post('/users', newUser);
      toast.success('Usuario creado exitosamente');
      setShowNewUser(false);
      setNewUser({ name: '', email: '', password: '', role: 'Usuario', areas: [] });
      fetchUsers();
    } catch (error) {
      toast.error(formatErrorMessage(error));
    }
  };

  const handleOpenEdit = (user) => {
    setSelectedUser(user);
    setEditForm({
      name: user.name,
      role: user.role,
      areas: user.areas || [],
      password: '',
      email: user.email || '',
    });
    setShowEditUser(true);
  };

  const handleUpdateUser = async () => {
    if (!selectedUser) return;
    
    try {
      const updateData = {
        name: editForm.name !== selectedUser.name ? editForm.name : undefined,
        role: editForm.role !== selectedUser.role ? editForm.role : undefined,
        areas: JSON.stringify(editForm.areas) !== JSON.stringify(selectedUser.areas) ? editForm.areas : undefined,
        password: editForm.password || undefined,
      };
      
      // Remove undefined values
      Object.keys(updateData).forEach(key => updateData[key] === undefined && delete updateData[key]);
      
      let changed = false;
      if (Object.keys(updateData).length > 0) {
        await api.put(`/users/${selectedUser.id}`, updateData);
        changed = true;
      }

      // Cambio de email va por endpoint dedicado con auditoría propia.
      const emailNormalizado = (editForm.email || '').trim().toLowerCase();
      const emailActual = (selectedUser.email || '').toLowerCase();
      if (emailNormalizado && emailNormalizado !== emailActual) {
        const confirmMsg = `¿Confirmas cambiar el email de "${selectedUser.name}"?\n\n` +
          `Actual: ${selectedUser.email}\nNuevo:  ${emailNormalizado}\n\n` +
          `El usuario deberá iniciar sesión con el nuevo email de ahora en adelante.`;
        if (!confirm(confirmMsg)) return;
        await api.patch(`/users/${selectedUser.id}/email`, { email: emailNormalizado });
        changed = true;
      }

      if (changed) toast.success('Usuario actualizado');
      setShowEditUser(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(formatErrorMessage(error));
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!confirm('¿Está seguro de eliminar este usuario?')) return;
    
    try {
      await api.delete(`/users/${userId}`);
      toast.success('Usuario eliminado');
      fetchUsers();
    } catch (error) {
      toast.error(formatErrorMessage(error));
    }
  };

  const toggleArea = (area, formSetter, currentAreas) => {
    const newAreas = currentAreas.includes(area)
      ? currentAreas.filter(a => a !== area)
      : [...currentAreas, area];
    formSetter(prev => ({ ...prev, areas: newAreas }));
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'Administrador': return 'bg-red-100 text-red-700';
      case 'Líder': return 'bg-blue-100 text-blue-700';
      case 'Coordinador': return 'bg-green-100 text-green-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const filteredUsers = users.filter(user =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />

      <div className="flex-1 p-8 overflow-y-auto max-h-screen">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <Settings className="w-10 h-10 text-primary" strokeWidth={1.5} />
                <h1 className="text-4xl font-bold text-slate-900" data-testid="panel-title">
                  Panel de Control
                </h1>
              </div>
              <p className="text-lg text-slate-600">
                Gestión de usuarios, roles y permisos
              </p>
            </div>
            <div className="flex gap-3">
              {/* Botón de Reseteo Total del Sistema */}
              <Button 
                variant="outline" 
                className="border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                onClick={() => setShowCleanupDialog(true)}
                data-testid="cleanup-data-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                🔄 Reseteo Total del Sistema
              </Button>
              
              <Dialog open={showNewUser} onOpenChange={setShowNewUser}>
              <DialogTrigger asChild>
                <Button className="bg-primary hover:bg-primary/90" data-testid="new-user-btn">
                  <UserPlus className="w-4 h-4 mr-2" />
                  Nuevo Usuario
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Crear Nuevo Usuario</DialogTitle>
                  <DialogDescription>Complete los datos del nuevo usuario</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <Label>Nombre Completo *</Label>
                    <Input
                      value={newUser.name}
                      onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                      placeholder="Juan Pérez"
                      data-testid="new-user-name"
                    />
                  </div>
                  <div>
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      value={newUser.email}
                      onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                      placeholder="usuario@peritum.cl"
                      data-testid="new-user-email"
                    />
                    <p className="text-xs text-slate-500 mt-1">Solo emails @peritum.cl</p>
                  </div>
                  <div>
                    <Label>Contraseña *</Label>
                    <Input
                      type="password"
                      value={newUser.password}
                      onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                      placeholder="••••••••"
                      data-testid="new-user-password"
                    />
                  </div>
                  <div>
                    <Label>Rol</Label>
                    <Select value={newUser.role} onValueChange={(value) => setNewUser({ ...newUser, role: value })}>
                      <SelectTrigger data-testid="new-user-role">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ROLES.map((role) => (
                          <SelectItem key={role.value} value={role.value}>
                            <span className="font-medium">{role.label}</span>
                            <span className="text-xs text-slate-500 ml-2">({role.description})</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="mb-2 block">Áreas Asignadas</Label>
                    <div className="grid grid-cols-2 gap-2">
                      {AREAS.map((area) => (
                        <div key={area.value} className="flex items-center gap-2">
                          <Checkbox
                            checked={newUser.areas.includes(area.value)}
                            onCheckedChange={() => toggleArea(area.value, setNewUser, newUser.areas)}
                            data-testid={`new-user-area-${area.value}`}
                          />
                          <span className="text-sm">{area.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <Button onClick={handleCreateUser} className="w-full" data-testid="create-user-btn">
                    Crear Usuario
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            </div>
          </div>

          {/* Dialog de Limpieza de Datos de Prueba */}
          <Dialog open={showCleanupDialog} onOpenChange={(open) => !open && resetCleanupDialog()}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-red-600">
                  <AlertTriangle className="w-5 h-5" />
                  🔄 RESETEO TOTAL DEL SISTEMA
                </DialogTitle>
              </DialogHeader>
              
              {cleanupStep === 1 && (
                <div className="space-y-4">
                  <Alert variant="destructive" className="bg-red-50 border-red-200">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription className="text-red-800">
                      <strong>⚠️ ¡ACCIÓN CRÍTICA E IRREVERSIBLE!</strong><br />
                      Esto eliminará TODOS los datos operativos del sistema.
                    </AlertDescription>
                  </Alert>
                  
                  <div className="bg-slate-50 p-4 rounded-lg space-y-3 text-sm max-h-[40vh] overflow-y-auto">
                    <p className="font-bold text-red-700 text-base">Se eliminarán PERMANENTEMENTE:</p>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="font-semibold text-slate-700 mb-1">📋 Comercial:</p>
                        <ul className="list-disc pl-5 text-slate-600 space-y-0.5 text-xs">
                          <li>Todas las Minutas de reuniones</li>
                          <li>Todas las PSP (Propuestas)</li>
                          <li>Todos los CPS (Contratos)</li>
                          <li>Todos los Clientes y Prospectos</li>
                          <li>Todos los Leads y Oportunidades</li>
                        </ul>
                      </div>
                      
                      <div>
                        <p className="font-semibold text-slate-700 mb-1">⚙️ Operaciones:</p>
                        <ul className="list-disc pl-5 text-slate-600 space-y-0.5 text-xs">
                          <li>Todas las Tareas (cualquier estado)</li>
                          <li>Tareas Pendientes de Asignar</li>
                          <li>Tareas en Revisión</li>
                          <li>Tareas Cumplidas</li>
                          <li>Todas las Alertas</li>
                        </ul>
                      </div>
                      
                      <div>
                        <p className="font-semibold text-slate-700 mb-1">💰 Finanzas:</p>
                        <ul className="list-disc pl-5 text-slate-600 space-y-0.5 text-xs">
                          <li>Todas las Facturas emitidas</li>
                          <li>Todas las Notas de Crédito (NC)</li>
                          <li>Todas las Notas de Débito (ND)</li>
                          <li>Gestiones de Cobranza</li>
                          <li>Minutas de Cobranza</li>
                          <li>Compromisos de Pago</li>
                          <li>Gastos y Cajas de Fondo</li>
                        </ul>
                      </div>
                      
                      <div>
                        <p className="font-semibold text-slate-700 mb-1">🏦 Conciliación:</p>
                        <ul className="list-disc pl-5 text-slate-600 space-y-0.5 text-xs">
                          <li>Movimientos Bancarios</li>
                          <li>Reglas de Clasificación</li>
                          <li>Cuentas Bancarias</li>
                          <li>KPIs y Resúmenes</li>
                          <li>Bitácoras e Historiales</li>
                        </ul>
                      </div>
                    </div>
                    
                    <div className="border-t pt-3 mt-3">
                      <p className="font-semibold text-green-700">✅ Se preservará:</p>
                      <ul className="list-disc pl-5 text-green-600 space-y-0.5 text-xs mt-1">
                        <li>Usuarios y configuración de roles</li>
                        <li>Estructura de módulos y tableros</li>
                        <li>Cliente con credenciales API activas</li>
                      </ul>
                    </div>
                  </div>
                  
                  <div className="flex gap-3 pt-4">
                    <Button 
                      variant="outline" 
                      onClick={resetCleanupDialog}
                      className="flex-1"
                    >
                      Cancelar
                    </Button>
                    <Button 
                      variant="destructive"
                      onClick={() => setCleanupStep(2)}
                      className="flex-1"
                      data-testid="cleanup-step1-confirm"
                    >
                      Entiendo, Continuar →
                    </Button>
                  </div>
                </div>
              )}
              
              {cleanupStep === 2 && (
                <div className="space-y-4">
                  <Alert variant="destructive" className="bg-red-100 border-red-300">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription className="text-red-900 font-medium">
                      ¿Estás seguro? Esta acción eliminará permanentemente todos los datos de prueba, PSPs y contratos creados hasta la fecha. <strong>No se pueden recuperar.</strong>
                    </AlertDescription>
                  </Alert>
                  
                  <div className="space-y-2">
                    <Label className="text-slate-700">
                      Ingrese su contraseña de Socio Administrador para confirmar:
                    </Label>
                    <Input
                      type="password"
                      value={cleanupPassword}
                      onChange={(e) => setCleanupPassword(e.target.value)}
                      placeholder="••••••••"
                      data-testid="cleanup-password-input"
                    />
                  </div>
                  
                  <div className="flex gap-3 pt-4">
                    <Button 
                      variant="outline" 
                      onClick={() => setCleanupStep(1)}
                      className="flex-1"
                      disabled={cleanupLoading}
                    >
                      Volver
                    </Button>
                    <Button 
                      variant="destructive"
                      onClick={handleCleanupData}
                      className="flex-1"
                      disabled={cleanupLoading || !cleanupPassword}
                      data-testid="cleanup-final-confirm"
                    >
                      {cleanupLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Limpiando...
                        </>
                      ) : (
                        <>
                          <Trash2 className="w-4 h-4 mr-2" />
                          Confirmar Limpieza
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}
              
              {cleanupStep === 3 && cleanupResults && (
                <div className="space-y-4">
                  <Alert className="bg-green-50 border-green-200">
                    <BadgeCheck className="h-4 w-4 text-green-600" />
                    <AlertDescription className="text-green-800">
                      Limpieza completada exitosamente
                    </AlertDescription>
                  </Alert>
                  
                  <div className="bg-slate-50 p-4 rounded-lg">
                    <p className="font-semibold text-slate-700 mb-3">Resumen de eliminación:</p>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-600">Minutas:</span>
                        <span className="font-medium">{cleanupResults.minutas_eliminadas}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600">PSP/CPS:</span>
                        <span className="font-medium">{cleanupResults.propuestas_eliminadas}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600">Clientes:</span>
                        <span className="font-medium">{cleanupResults.clientes_eliminados}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600">Tareas:</span>
                        <span className="font-medium">{cleanupResults.tareas_eliminadas}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600">Alertas:</span>
                        <span className="font-medium">{cleanupResults.alertas_eliminadas}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600">Leads:</span>
                        <span className="font-medium">{cleanupResults.leads_eliminados}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="text-xs text-slate-500 bg-blue-50 p-3 rounded">
                    <strong>Nota:</strong> Los dashboards de cmoreno, jcarrasco y gcarrasco ahora mostrarán $0 en "En Negociación" y "PSP Aceptadas".
                  </div>
                  
                  <Button 
                    onClick={resetCleanupDialog}
                    className="w-full"
                    data-testid="cleanup-close-btn"
                  >
                    Cerrar
                  </Button>
                </div>
              )}
            </DialogContent>
          </Dialog>

          {/* Search */}
          <div className="mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar por nombre o email..."
                className="pl-10"
                data-testid="search-users"
              />
            </div>
          </div>

          {/* Users List */}
          {loading ? (
            <Card className="p-12 text-center">
              <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4" />
              <p className="text-slate-600">Cargando usuarios...</p>
            </Card>
          ) : filteredUsers.length === 0 ? (
            <Card className="p-12 text-center">
              <Users className="w-16 h-16 text-slate-400 mx-auto mb-4" />
              <p className="text-lg text-slate-600">No hay usuarios</p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredUsers.map((user) => (
                <Card key={user.id} className="p-4 border-slate-200" data-testid={`user-card-${user.id}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <span className="text-primary font-bold">
                          {user.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">{user.name}</h3>
                        <p className="text-sm text-slate-500">{user.email}</p>
                      </div>
                    </div>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getRoleColor(user.role)}`}>
                      {user.role}
                    </span>
                  </div>
                  
                  {user.areas && user.areas.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {user.areas.map((area) => (
                        <span key={area} className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded">
                          {area}
                        </span>
                      ))}
                    </div>
                  )}
                  
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={() => handleOpenEdit(user)}
                      data-testid={`edit-user-${user.id}`}
                    >
                      <Edit2 className="w-4 h-4 mr-1" />
                      Editar
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => handleDeleteUser(user.id)}
                      data-testid={`delete-user-${user.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}

          {/* Roles Info */}
          <Card className="mt-8 p-6 border-slate-200">
            <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5 text-primary" />
              Permisos por Rol
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {ROLES.map((role) => (
                <div key={role.value} className={`p-4 rounded-lg ${getRoleColor(role.value)} bg-opacity-20`}>
                  <h4 className="font-semibold mb-1">{role.label}</h4>
                  <p className="text-sm opacity-80">{role.description}</p>
                </div>
              ))}
            </div>
          </Card>

          {/* Matriz de Permisos Granulares */}
          <div className="mt-8">
            <PermisosMatrix currentUser={(() => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; } })()} />
          </div>

          {/* Configuración Corporativa (razón social, socios, alertas, branding) */}
          <div className="mt-8">
            <CompanyConfigTab currentUser={(() => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; } })()} />
          </div>

          {/* Importación de Ventas Históricas */}
          <div className="mt-8">
            <ImportVentasTab currentUser={(() => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; } })()} />
          </div>

          {/* Fichas Técnicas descargables (PDF) */}
          <div className="mt-8">
            <FichasTecnicasCard />
          </div>
        </motion.div>
      </div>

      {/* Edit User Modal */}
      <Dialog open={showEditUser} onOpenChange={setShowEditUser}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Editar Usuario</DialogTitle>
            <DialogDescription>Modifique los datos del usuario</DialogDescription>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-4 mt-4">
              <div>
                <Label>Nombre Completo</Label>
                <Input
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  data-testid="edit-user-name"
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  placeholder="usuario@peritum.cl"
                  data-testid="edit-user-email"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Solo dominio @peritum.cl. Cambiarlo actualiza el email de inicio de sesión.
                  No puedes cambiar tu propio email; pide a otro administrador que lo actualice.
                </p>
              </div>
              <div>
                <Label>Nueva Contraseña (opcional)</Label>
                <Input
                  type="password"
                  value={editForm.password}
                  onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                  placeholder="Dejar vacío para mantener"
                  data-testid="edit-user-password"
                />
              </div>
              <div>
                <Label>Rol</Label>
                <Select value={editForm.role} onValueChange={(value) => setEditForm({ ...editForm, role: value })}>
                  <SelectTrigger data-testid="edit-user-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLES.map((role) => (
                      <SelectItem key={role.value} value={role.value}>
                        {role.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="mb-2 block">Áreas Asignadas</Label>
                <div className="grid grid-cols-2 gap-2">
                  {AREAS.map((area) => (
                    <div key={area.value} className="flex items-center gap-2">
                      <Checkbox
                        checked={editForm.areas.includes(area.value)}
                        onCheckedChange={() => toggleArea(area.value, setEditForm, editForm.areas)}
                      />
                      <span className="text-sm">{area.label}</span>
                    </div>
                  ))}
                </div>
              </div>
              <Button onClick={handleUpdateUser} className="w-full" data-testid="update-user-btn">
                Guardar Cambios
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PanelControl;
